import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Stack,
  Container,
  TableContainer,
  Paper,
} from '@mui/material';
import { getPropertyManagementRequests } from '../../api/admin';
import { toast } from 'react-toastify';

interface PropertyManagement {
  _id: string;
  name: string;
  email: string;
  phone: string;
  propertyType: string;
  numberOfProperties: number;
  address: string;
  totalTenants: number;
  propertyOwnership: string;
  vacantRooms: number;
  preferredTenants: string;
  needFinancialSupport: boolean;
  createdAt: string;
}

const PropertyManagementRequests: React.FC = () => {
  const [requests, setRequests] = useState<PropertyManagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchRequests = async (page: number) => {
    try {
      setLoading(true);
      const response = await getPropertyManagementRequests(page);
      setRequests(response.propertyManagements);
      setTotalPages(response.pagination.pages);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch property management requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(currentPage);
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Stack spacing={4}>
          <Typography variant="h4" component="h1">
            Property Management Requests
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Owner Details</TableCell>
                  <TableCell>Property Type</TableCell>
                  <TableCell>Properties</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Total Tenants</TableCell>
                  <TableCell>Ownership</TableCell>
                  <TableCell>Vacant Rooms</TableCell>
                  <TableCell>Preferred Tenants</TableCell>
                  <TableCell>Financial Support</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request._id}>
                    <TableCell>
                      <Typography variant="subtitle2">{request.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {request.email}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {request.phone}
                      </Typography>
                    </TableCell>
                    <TableCell>{request.propertyType}</TableCell>
                    <TableCell>{request.numberOfProperties}</TableCell>
                    <TableCell>{request.address}</TableCell>
                    <TableCell>{request.totalTenants}</TableCell>
                    <TableCell>{request.propertyOwnership}</TableCell>
                    <TableCell>{request.vacantRooms}</TableCell>
                    <TableCell>{request.preferredTenants}</TableCell>
                    <TableCell>
                      <Chip
                        label={request.needFinancialSupport ? 'Yes' : 'No'}
                        color={request.needFinancialSupport ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(request.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={2}>
            <Stack direction="row" spacing={1}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? 'contained' : 'outlined'}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </Button>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
};

export default PropertyManagementRequests; 